import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  NgbCalendar,
  NgbDate,
  NgbDatepicker,
  NgbDatepickerI18n,
  NgbDatepickerNavigateEvent,
  NgbDateStruct
} from '@ng-bootstrap/ng-bootstrap';
import { FormattedDate } from '../../../../models/shared/formatted-date';
import { DropdownInputComponent } from '../../dropdown-input/dropdown-input.component';
import { DropDownItem } from '../../../../models/shared/stylesheet/drop-down-item';

export interface DateSelection {
  startDate: FormattedDate | null;
  endDate: FormattedDate | null;
}

const DEFAULT_MIN_DATE = new NgbDate(1930, 1, 1);
const DEFAULT_MAX_YEAR_OFFSET = 10;

@Component({
  selector: 'app-datepicker-input-calendar',
  templateUrl: './datepicker-input-calendar.component.html',
  styleUrls: ['./datepicker-input-calendar.component.scss'],
  standalone: true,
  imports: [NgIf, FormsModule, NgbDatepicker, DropdownInputComponent],
  changeDetection: ChangeDetectionStrategy.Default
})
export class DatepickerInputCalendarComponent implements OnChanges {
  @Input() dateRange = false;
  @Input() isStartDate: boolean = true;
  @Input() initialSelectedDates: DateSelection | null = null;
  @Input() minDate: string | null = null;
  @Input() maxDate: string | null = null;

  @Output() dateSelected = new EventEmitter<DateSelection>();

  private ngbCalendar = inject(NgbCalendar);
  private ngbDatepickerI18n = inject(NgbDatepickerI18n);

  public hoveredDate: NgbDate | null = null;
  public selectedDate: NgbDateStruct | null = null;
  public fromDate: NgbDate | null = null;
  private toDate: NgbDate | null = null;
  private fromDatePickedHere = false;

  public today = this.ngbCalendar.getToday();

  private readonly defaultMaxDate = new NgbDate(this.today.year + DEFAULT_MAX_YEAR_OFFSET, 12, 31);

  public minNgbDate: NgbDate = DEFAULT_MIN_DATE;
  public maxNgbDate: NgbDate = this.defaultMaxDate;

  public readonly useNativeNavigation = matchMedia('(pointer: coarse)').matches;
  public readonly navigationMode: 'select' | 'none' = this.useNativeNavigation ? 'select' : 'none';

  // The month/year currently shown by the calendar — drives the custom dropdowns' selected value.
  public viewDate: NgbDate = this.today;
  public yearOptions: DropDownItem[] = [];
  public monthOptions: DropDownItem[] = [];
  public canGoToPreviousMonth = true;
  public canGoToNextMonth = true;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.initialSelectedDates && !!this.initialSelectedDates) {
      this.fromDate = this.initialSelectedDates.startDate?.ngbDate ?? null;
      this.toDate = this.initialSelectedDates.endDate?.ngbDate ?? null;
      this.fromDatePickedHere = false;
    }
    if (changes.initialSelectedDates || changes.minDate || changes.maxDate) {
      this.minNgbDate = this.resolveMinDate();
      this.maxNgbDate = this.resolveMaxDate();
      this.viewDate = this.fromDate ?? this.today;
      this.refreshNavigationOptions();
    }
  }

  private resolveMinDate(): NgbDate {
    // An incoming min date always wins — dates below it are genuinely invalid.
    if (this.minDate) {
      return FormattedDate.fromUtcString(this.minDate).ngbDate ?? DEFAULT_MIN_DATE;
    }

    // Otherwise fall back to 1930, but never clip a date the user has actually entered.
    let earliest = DEFAULT_MIN_DATE;
    for (const selected of [this.fromDate, this.toDate]) {
      if (selected && selected.before(earliest)) {
        earliest = selected;
      }
    }
    return earliest;
  }

  private resolveMaxDate(): NgbDate {
    // An incoming max date always wins — dates above it are genuinely invalid.
    if (this.maxDate) {
      return FormattedDate.fromUtcString(this.maxDate).ngbDate ?? this.defaultMaxDate;
    }

    // Otherwise fall back to a decade past today, but never clip a date the user has actually entered.
    let latest = this.defaultMaxDate;
    for (const selected of [this.fromDate, this.toDate]) {
      if (selected && selected.after(latest)) {
        latest = selected;
      }
    }
    return latest;
  }

  private refreshNavigationOptions(): void {
    this.yearOptions = this.buildYearOptions();
    this.monthOptions = this.buildMonthOptions();
    const viewMonth = this.firstOfMonth(this.viewDate);
    this.canGoToPreviousMonth = viewMonth.after(this.firstOfMonth(this.minNgbDate));
    this.canGoToNextMonth = viewMonth.before(this.firstOfMonth(this.maxNgbDate));
  }

  private buildYearOptions(): DropDownItem[] {
    const options: DropDownItem[] = [];
    for (let year = this.minNgbDate.year; year <= this.maxNgbDate.year; year++) {
      options.push(new DropDownItem(`${year}`, year));
    }
    return options;
  }

  private buildMonthOptions(): DropDownItem[] {
    // Boundary years only offer the months inside the min/max range.
    const firstMonth = this.viewDate.year === this.minNgbDate.year ? this.minNgbDate.month : 1;
    const lastMonth = this.viewDate.year === this.maxNgbDate.year ? this.maxNgbDate.month : 12;

    const options: DropDownItem[] = [];
    for (let month = firstMonth; month <= lastMonth; month++) {
      options.push(new DropDownItem(this.ngbDatepickerI18n.getMonthShortName(month), month));
    }
    return options;
  }

  private firstOfMonth(date: NgbDate): NgbDate {
    return new NgbDate(date.year, date.month, 1);
  }

  // Keeps the requested month inside the min/max bounds for the target year (e.g. jumping to the
  // min year while a later month is showing should snap up to the earliest selectable month).
  private clampMonthToBounds(year: number, month: number): number {
    if (year === this.minNgbDate.year && month < this.minNgbDate.month) {
      return this.minNgbDate.month;
    }
    if (year === this.maxNgbDate.year && month > this.maxNgbDate.month) {
      return this.maxNgbDate.month;
    }
    return month;
  }

  public onDatepickerNavigate(event: NgbDatepickerNavigateEvent): void {
    this.viewDate = new NgbDate(event.next.year, event.next.month, 1);
    this.refreshNavigationOptions();
  }

  public onYearSelected(datepicker: NgbDatepicker, selection: DropDownItem): void {
    const year = selection.getSelectionValue() as number;
    const month = this.clampMonthToBounds(year, this.viewDate.month);
    datepicker.navigateTo(new NgbDate(year, month, 1));
  }

  public onMonthSelected(datepicker: NgbDatepicker, selection: DropDownItem): void {
    const month = selection.getSelectionValue() as number;
    datepicker.navigateTo(new NgbDate(this.viewDate.year, month, 1));
  }

  private dateIsDisabled(date: NgbDate): boolean {
    return date.before(this.minNgbDate) || date.after(this.maxNgbDate);
  }

  public handleDateSelection(date: NgbDate): void {
    if (this.dateRange) {
      this.handleDateRangeSelection(date);
    } else {
      this.selectedDate = date;
      this.fromDate = date;
    }
  }

  public handleDateRangeSelection(date: NgbDate): void {
    if (this.isBuildingRange() && date.after(this.fromDate)) {
      this.toDate = date;
    } else if (this.movesStart()) {
      this.fromDate = date;
      this.fromDatePickedHere = true;
    } else {
      this.toDate = date;
    }
  }

  // A start date carried in from the partner input only anchors a range in the end picker. In the
  // start picker it is the value being changed, so a second click there moves the start instead
  // of closing a range, unless the start was picked in this same calendar.
  private isBuildingRange(): boolean {
    return !!this.fromDate && !this.toDate && (this.fromDatePickedHere || !this.isStartDate);
  }

  // The start picker always moves the start. The end picker only does so while nothing is set yet,
  // or to re-anchor a start it picked itself when the next click lands on or before it. A start
  // carried in from the partner input is never moved from the end picker.
  private movesStart(): boolean {
    return this.isStartDate || (!this.toDate && (!this.fromDate || this.fromDatePickedHere));
  }

  public isHovered(date: NgbDate): boolean {
    if (this.dateRange && !this.dateIsDisabled(date)) {
      return this.isBuildingRange() && !!this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate);
    }

    return false;
  }

  public isInside(date: NgbDate): boolean {
    return !!this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  public isFocused(date: NgbDate, focused: boolean): boolean {
    return date.equals(this.selectedDate) || focused;
  }

  public isRange(date: NgbDate): boolean {
    return (
      date.equals(this.fromDate) ||
      (this.toDate && date.equals(this.toDate)) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  public disableToday = (date: NgbDateStruct) => {
    return date.year === this.today.year && date.month === this.today.month && date.day === this.today.day;
  };

  public navigate(datepicker: NgbDatepicker, num: number): void {
    const { state, calendar } = datepicker;
    datepicker.navigateTo(calendar.getNext(state.focusedDate, 'm', num));
  }

  public emitDateSelection(): void {
    if (!this.isStartDate && !this.toDate && this.fromDatePickedHere) {
      this.toDate = this.fromDate;
      this.fromDate = null;
    }

    const formattedFromDate = this.fromDate ? FormattedDate.fromNgbDate(this.fromDate) : null;
    const formattedToDate = this.toDate ? FormattedDate.fromNgbDate(this.toDate) : null;

    this.dateSelected.emit({
      startDate: formattedFromDate,
      endDate: formattedToDate
    });
  }

  public clear(): void {
    this.toDate = null;
    this.selectedDate = null;
    this.fromDate = null;
    this.fromDatePickedHere = false;
    this.selectedDate = null;
  }
}
